/**
 * ModelStatusProvider / useModelStatus — the state half of the content/state split (plan
 * thinking/plans/2026_08_11_content_state_split.md, Phase 1 steps 1-3). Replaces the ambient
 * "every page revalidates the whole router tree every 3s" pattern — the shared revalidation-timer
 * hook this pass deletes — with ONE context, mounted ONCE in each product's layout route, that
 * polls a single small resource route (`/api/model-status`) instead of re-running every ancestor
 * loader on the page.
 *
 * Streaming-pattern contract (`app/products/narcis/CLAUDE.md`): `useFetcher`, never a
 * `useNavigation`-coupled mechanism — `fetcher.load()` only ever touches THIS fetcher's own state,
 * so a model-status tick can never collide with an unrelated in-flight navigation or another
 * fetcher's submission the way the old router-global `revalidate()` did (2026-07-26 incident: seven
 * spurious 500s on one pack page from two ambient polls racing the same shared revalidate call).
 *
 * Arming: `isModelStatusArmed` (`./modelStatusArming.ts`, pure, independently tested) — training,
 * retraining, or ready-but-undelivered. The FIRST arming decision is seeded from the root loader's
 * own `userModelStatus` (`useRouteLoaderData('root')`) so polling starts immediately on mount
 * rather than waiting a full interval for the first fetch to even begin — but the exposed `model`
 * value itself stays `null` until a real `/api/model-status` response lands. Consumers merge
 * `useModelStatus() ?? <their own loader's already-full seed>` — their own route loader already
 * supplies a complete, correctly-typed first paint; this context only ever supersedes it with a
 * strictly LARGER, equally-complete payload, never a partial one that could silently drop a field
 * a consumer reads (e.g. `trainedAt` for `PackModelChip`'s date formatting).
 */
import {createContext, useContext, useEffect, useState} from 'react'
import {useFetcher, useRouteLoaderData} from 'react-router'
import type {loader as rootLoader} from '@root'
import {isModelStatusArmed} from './modelStatusArming'
import type {ModelStatusPayload} from './modelStatusTypes'

export type {ModelStatusPayload} from './modelStatusTypes'

const POLL_INTERVAL_MS = 3000

const ModelStatusContext = createContext<ModelStatusPayload | null | undefined>(undefined)

export interface ModelStatusProviderProps {
    /** The product's own `/api/model-status` resource route — narcis and ovid each mount this
     *  Provider once (in their `_layout.tsx`) pointed at their own route. */
    url: string
    children: React.ReactNode
}

export function ModelStatusProvider({url, children}: ModelStatusProviderProps) {
    // Root's own userModelStatus projection is partial (status/percent/delivered only — see
    // root.tsx) but sufficient to decide whether to arm before the first real fetch resolves.
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const rootSeed = (rootData as {userModelStatus?: {status: 'training' | 'ready' | 'failed'; percent?: number; delivered?: boolean} | null} | undefined)?.userModelStatus ?? null

    const [model, setModel] = useState<ModelStatusPayload | null>(null)
    const fetcher = useFetcher<{model: ModelStatusPayload | null}>()

    useEffect(() => {
        if (fetcher.data !== undefined) setModel(fetcher.data.model)
    }, [fetcher.data])

    // Once a real poll has landed, its own model is the authoritative arming source (it may be
    // MORE current than root's per-request snapshot — e.g. a retrain that started after this page
    // loaded). Before that, fall back to root's seed so the very first interval doesn't miss a
    // model that was already training when this page loaded.
    const armingSource = model ?? rootSeed
    const armed = isModelStatusArmed(armingSource)

    useEffect(() => {
        if (!armed) return
        function tick() {
            if (document.visibilityState === 'visible') fetcher.load(url)
        }
        const timer = setInterval(tick, POLL_INTERVAL_MS)
        document.addEventListener('visibilitychange', tick)
        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', tick)
        }
        // fetcher.load's identity is not stable across renders (react-router mints a new fetcher
        // callback set per render in some versions) — omitting it from deps is deliberate: the
        // effect only needs to re-arm on `armed`/`url` changes, not on every fetcher re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [armed, url])

    return <ModelStatusContext.Provider value={model}>{children}</ModelStatusContext.Provider>
}

/** The latest known model status, or `null` while unarmed / before the first poll resolves.
 *  Callers merge `useModelStatus() ?? <their own loader's seed>` — see file header. */
export function useModelStatus(): ModelStatusPayload | null {
    const ctx = useContext(ModelStatusContext)
    if (ctx === undefined) {
        throw new Error('useModelStatus() called outside a ModelStatusProvider')
    }
    return ctx
}
